import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, CaseSensitive, Check, Clock, Flag, History, RotateCcw, Settings2, Square, Trophy, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import mainSignalSound from '../../../assets/sound/main-signal.mp3';
import { useInitialsGame } from '../../../hooks/useInitialsGame';
import { playSoundCue, preloadSoundCues } from '../../../utils/soundCues';
import { describeRule } from '../../../utils/initials';
import { currentRound, GameState, Round, RoundEvent, SideId, sideName, usedPersonKeys } from '../../../utils/initialsGame';
import BuzzerStage from '../Buzzer/BuzzerStage';
import InitialsPick from './InitialsPick';
import InitialsSetup from './InitialsSetup';
import InitialsTurn from './InitialsTurn';
import { Btn, LetterPair, Panel, PanelTitle } from './parts';

const STORAGE_KEY = '3sual.initials.state';

const ghostBtnStyle: React.CSSProperties = {
  borderRadius: '0.75rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
};

const Scoreboard: React.FC<{ state: GameState }> = ({ state }) => {
  const sides: SideId[] = ['a', 'b'];
  const active = state.phase === 'turn' || state.phase === 'review' ? state.turn : null;
  return (
    <div className="grid grid-cols-2 gap-3">
      {sides.map(side => (
        <Panel key={side} className={cn('flex items-center gap-3 px-4 py-3 transition', active === side && 'border-sky-400/50 bg-sky-500/10')}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold">{sideName(state, side)}</p>
            {state.settings.sides[side].members.trim() && <p className="truncate text-xs text-white/40">{state.settings.sides[side].members}</p>}
          </div>
          <span className="text-3xl font-black tabular-nums">{state.wins[side]}</span>
        </Panel>
      ))}
      <p className="col-span-2 text-center text-xs text-white/40">
        {state.settings.targetWins > 0 ? `${state.settings.targetWins} qələbəyədək` : 'Limitsiz'}
        {state.draws > 0 && ` · heç-heçə: ${state.draws}`}
      </p>
    </div>
  );
};

const EventRow: React.FC<{ state: GameState; event: RoundEvent }> = ({ state, event }) => {
  if (event.kind === 'failed') {
    return (
      <li className="flex items-center gap-2 text-sm text-white/50">
        {event.reason === 'timeout' ? <Clock className="h-4 w-4 text-rose-300" /> : <Flag className="h-4 w-4 text-rose-300" />}
        {sideName(state, event.side)} — {event.reason === 'timeout' ? 'vaxt bitdi' : 'tapa bilmədi'}
      </li>
    );
  }
  const { answer } = event;
  const accepted = event.kind === 'accepted';
  return (
    <li className={cn('flex items-start gap-2 text-sm', !accepted && 'opacity-60')}>
      {accepted ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />}
      <div className="min-w-0">
        <p>
          <span className={cn('font-bold', !accepted && 'line-through')}>{answer.fullName}</span>
          {answer.original && <span className="text-white/40"> ({answer.original})</span>}
          <span className="text-white/40"> — {sideName(state, answer.side)}</span>
        </p>
        <p className="text-white/50">{answer.description}</p>
        {event.kind === 'rejected' && <p className="text-rose-300/80">Etiraz{event.reason ? `: ${event.reason}` : ''}</p>}
      </div>
    </li>
  );
};

const RoundLog: React.FC<{ state: GameState; round: Round; title?: string }> = ({ state, round, title }) => (
  <Panel>
    <PanelTitle icon={<History className="h-5 w-5 text-sky-300" />} aside={<LetterPair name={round.letters.name} surname={round.letters.surname} className="text-lg" />}>
      {title ?? `Raund ${round.no}`}
    </PanelTitle>
    <div className="p-4 sm:p-5">
      <p className="mb-3 text-xs text-white/40">{describeRule(round.rule)}</p>
      {round.events.length === 0 ? (
        <p className="text-sm text-white/30">Hələ heç kim deyilməyib.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {round.events.map((event, i) => (
            <EventRow key={i} state={state} event={event} />
          ))}
        </ul>
      )}
    </div>
  </Panel>
);

const winnerText = (state: GameState, winner: Round['winner']) => (winner === 'draw' ? 'Heç-heçə' : winner ? `${sideName(state, winner)} qazandı` : '');

/**
 * "İnisiallar": players name real people whose name and surname fit two picked
 * letters. The system runs turns and the timer, checks letters and repeats,
 * and lets the opponent confirm that the person is real. Everything happens
 * on one shared screen and stays in this browser.
 */
const Initials: React.FC = () => {
  const { state, dispatch, remainingMs, running } = useInitialsGame(STORAGE_KEY);
  const round = currentRound(state);
  const usedKeys = useMemo(() => usedPersonKeys(state), [state]);

  useEffect(() => preloadSoundCues(mainSignalSound), []);

  // Signal every time a turn is lost to the clock.
  const timeouts = state.rounds.reduce((n, r) => n + r.events.filter(e => e.kind === 'failed' && e.reason === 'timeout').length, 0);
  const seenTimeouts = useRef(timeouts);
  useEffect(() => {
    if (timeouts > seenTimeouts.current) playSoundCue(mainSignalSound);
    seenTimeouts.current = timeouts;
  }, [timeouts]);

  const finishedRounds = state.rounds.filter(r => r.winner !== null).reverse();
  const inGame = state.phase !== 'setup';

  const confirmThen = (message: string, action: () => void) => {
    if (window.confirm(message)) action();
  };

  return (
    <BuzzerStage>
      <Helmet>
        <title>İnisiallar | 3sual.az</title>
      </Helmet>

      <header className="relative z-10 flex items-center justify-between gap-3 px-4 pb-3 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-10 sm:pt-[calc(1.75rem+env(safe-area-inset-top))]">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center sm:h-10 sm:w-10"
            style={{ borderRadius: '0.75rem', background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.25)' }}
          >
            <CaseSensitive className="h-4 w-4 text-amber-300 sm:h-5 sm:w-5" />
          </div>
          <h1 className="truncate text-xl font-black text-white sm:text-2xl">İnisiallar</h1>
        </div>
        <div className="flex items-center gap-2">
          {inGame && (
            <>
              <button
                type="button"
                onClick={() => dispatch({ type: 'undo' })}
                disabled={state.past.length === 0}
                title="Son addımı geri al"
                style={ghostBtnStyle}
                className="flex h-9 items-center gap-1.5 px-3 text-sm font-semibold text-white/60 transition hover:text-white disabled:opacity-30 sm:h-10"
              >
                <Undo2 className="h-4 w-4" />
                <span className="hidden sm:inline">Geri al</span>
              </button>
              {state.phase !== 'matchOver' && (
                <button
                  type="button"
                  onClick={() => confirmThen('Oyunu bitirmək istəyirsiniz?', () => dispatch({ type: 'finishMatch' }))}
                  title="Oyunu bitir"
                  style={ghostBtnStyle}
                  className="flex h-9 items-center gap-1.5 px-3 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10"
                >
                  <Square className="h-4 w-4" />
                  <span className="hidden sm:inline">Bitir</span>
                </button>
              )}
            </>
          )}
          <Link
            to="/tools"
            title="Bağla"
            aria-label="Bağla"
            style={ghostBtnStyle}
            className="flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10 sm:w-auto sm:px-3"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">Bağla</span>
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:px-10">
        {state.phase === 'setup' && <InitialsSetup initial={state.settings} onStart={settings => dispatch({ type: 'startMatch', settings })} />}

        {inGame && <Scoreboard state={state} />}

        {state.phase === 'pick' && <InitialsPick key={state.rounds.length} state={state} dispatch={dispatch} />}

        {(state.phase === 'turn' || state.phase === 'review') && round && (
          <>
            <InitialsTurn state={state} dispatch={dispatch} remainingMs={remainingMs} running={running} usedKeys={usedKeys} />
            <RoundLog state={state} round={round} title="Bu raund" />
          </>
        )}

        {state.phase === 'roundOver' && round && (
          <>
            <Panel className="flex flex-col items-center gap-3 px-4 py-6 text-center">
              <Trophy className="h-10 w-10 text-amber-300" />
              <p className="text-3xl font-black">{winnerText(state, round.winner)}</p>
              <Btn className="px-6 py-3 text-base" onClick={() => dispatch({ type: 'nextRound' })}>
                Növbəti raund
                <ArrowRight className="h-4 w-4" />
              </Btn>
            </Panel>
            <RoundLog state={state} round={round} />
          </>
        )}

        {state.phase === 'matchOver' && (
          <Panel className="flex flex-col items-center gap-3 px-4 py-6 text-center">
            <Trophy className="h-12 w-12 text-amber-300" />
            <p className="text-sm font-semibold uppercase tracking-wider text-white/40">Oyun bitdi</p>
            <p className="text-3xl font-black">
              {state.wins.a === state.wins.b ? 'Bərabərlik' : `${sideName(state, state.wins.a > state.wins.b ? 'a' : 'b')} qalib gəldi`}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Btn onClick={() => dispatch({ type: 'startMatch', settings: state.settings })}>
                <RotateCcw className="h-4 w-4" />
                Yenidən oyna
              </Btn>
              <Btn tone="ghost" onClick={() => dispatch({ type: 'newMatch' })}>
                <Settings2 className="h-4 w-4" />
                Ayarları dəyiş
              </Btn>
            </div>
          </Panel>
        )}

        {inGame && state.phase !== 'roundOver' && finishedRounds.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/40">Keçmiş raundlar</h2>
            {finishedRounds.map(r => (
              <RoundLog key={r.no} state={state} round={r} title={`Raund ${r.no} — ${winnerText(state, r.winner)}`} />
            ))}
          </section>
        )}
      </main>
    </BuzzerStage>
  );
};

export default Initials;
